/**
 * Schedule Controls store getters. Kept apart from scheduleControls.ts, which the schedule pages' helpers live in,
 * because the store imports this module: in development, editing a module the store imports hot-reloads the store,
 * which drops modules registered at startup (e.g. jwt) until the page is reloaded.
 */

export interface ScheduleControlsState {
  features: string[]
  permissions: string[]
  // Older servers don't send these session keys, so they may be undefined.
  budgetSeverity?: string | null
  enablePeriodApproval?: boolean | null
}

export interface ScheduleControlsGetters {
  scheduleEnabled: boolean
  hasScheduleControlsFeature: boolean
  canManageSchedule: boolean
}

export const hasScheduleControlsFeature = (state: ScheduleControlsState): boolean =>
  state.features.includes('schedule_controls')

// Approving needs manage_schedule too; the employee form enforces that, but older records may not.
export const canApproveSchedule = (state: ScheduleControlsState, getters: Pick<ScheduleControlsGetters, 'canManageSchedule'>): boolean =>
  getters.canManageSchedule && state.permissions.includes('approve_schedule')

export const scheduleBudgetEnabled = (state: ScheduleControlsState, getters: ScheduleControlsGetters): boolean =>
  getters.scheduleEnabled && getters.hasScheduleControlsFeature && !!state.budgetSeverity && state.budgetSeverity !== 'off'

export const schedulePeriodApprovalEnabled = (state: ScheduleControlsState, getters: ScheduleControlsGetters): boolean =>
  getters.scheduleEnabled && getters.hasScheduleControlsFeature && !!state.enablePeriodApproval

/** Budgets matter when budget warnings are on, and when approval is, since approvers review hours against them. */
export const scheduleBudgetsInUse = (_state: ScheduleControlsState, getters: { scheduleBudgetEnabled: boolean, schedulePeriodApprovalEnabled: boolean }): boolean =>
  getters.scheduleBudgetEnabled || getters.schedulePeriodApprovalEnabled
